import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import React, { Fragment, FunctionComponent, useContext } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import ROUTES from '../../../routes';
import colors from '../../../themes/main-colors';
import {
  toDateString,
  toCurrencyString,
  toCurrencyDisplayFormat,
  toSentenceCase,
} from '../../../util/format';
import {
  BillInfo,
  AutoPayInfo,
  ViewBillDetails,
  AccountType,
} from '../../../__generated__/pge-types';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import { LanguageContext } from '../../../providers/LanguageProvider';
export interface ResCurrentBillPaymentSummaryProps {
  amountCreditDisplay: 'CR' | 'Zero';
  autoPay: AutoPayInfo | null;
  billInfo: BillInfo | null | undefined;
  isMobile: boolean;
  viewBillDetails: ViewBillDetails | null | undefined;
  isPendingDisconnect: boolean | undefined;
}

function buildPaymentMessage(billInfo: BillInfo | null | undefined) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const {
    oneTimeFuturePaymentScheduled,
    multipleFuturePaymentsScheduled,
    lastPaymentDate,
    lastPaymentAmount,
    futurePaymentDate,
  } = billInfo || {};

  if (oneTimeFuturePaymentScheduled && Boolean(futurePaymentDate)) {
    const futureMessage = t('LAST_PAYMENT_ONE_FUTURE', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(lastPaymentDate || ''),
      FUTURE_AMOUNT: toCurrencyString(lastPaymentAmount || 0, false),
      FUTURE_DATE: toDateString(futurePaymentDate || ''),
    });

    if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
      return `${t('LAST_PAYMENT_OF', {
        AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
        DATE_OF_PAYMENT: toDateString(
          lastPaymentDate || '',
          'MMM. dd, yyyy',
          language,
        ),
      })} ${futureMessage}`;
    } else {
      return futureMessage;
    }
  } else if (multipleFuturePaymentsScheduled) {
    return t('LAST_PAYMENT_MULTIPLE_FUTURE');
  } else if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
    return t('LAST_PAYMENT_OF', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(
        lastPaymentDate || '',
        'MMM. dd, yyyy',
        language,
      ),
    });
  }

  return t('LAST_PAYMENT_YET_TO_BE_BILLED');
}

function buildPaymentDate(
  billInfo: BillInfo | null | undefined,
  autoPay: AutoPayInfo | null,
) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const isEnrolledInAutopay = Boolean(autoPay?.isEnrolled);
  if (isEnrolledInAutopay && autoPay?.draftDate) {
    return {
      title: t('WILL_BE_DEDUCTED'),
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : '---',
    };
  } else {
    return {
      title: t('DUE_DATE'),
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : 'Not yet available',
    };
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: colors.lightBlue1,
      paddingTop: '16px',
      paddingBottom: '16px',
      borderRadius: '3px 3px 0px 0px',
      marginTop: '2px',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    datesStyles: {
      color: colors.noirBlur,
      '& p': {
        marginTop: '0px !important',
      },
    },
    colorTextPastdue: {
      color: colors.roseRed,
      fontWeight: 'bold',
    },
    columnview: {
      padding: '16px 16px 0 16px',
    },
    mobileColumnview: {
      padding: '16px 0 0 0',
    },
    boxContainer: {
      backgroundColor: colors.white,
      padding: theme.spacing(2),
      textAlign: 'center',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
    },
    autoPayLinkStyle: {
      marginTop: '1rem',
      textAlign: 'center',
    },
    tempBillingMessage: {
      fontSize: '1rem',
      padding: '1rem 1rem 0 1rem',
    },
    tempBillingMessageMob: {
      fontSize: '1rem',
      paddingTop: '1rem',
    },
  }),
);

const ResCurrentBillPaymentSummary: FunctionComponent<ResCurrentBillPaymentSummaryProps> = ({
  billInfo,
  autoPay,
  amountCreditDisplay,
  viewBillDetails,
  isPendingDisconnect,
  isMobile,
}) => {
  const billDetailsDueDate = billInfo?.billDetails?.dueDate
    ? new Date(billInfo?.billDetails?.dueDate)
    : null;
  const autoPayDate1 = new Date(2025, 1, 22);
  const autoPaydate2 = new Date(2025, 2, 6);
  const isDateBetweenAutoPayDates: boolean = !!(
    billDetailsDueDate &&
    billDetailsDueDate >= autoPayDate1 &&
    billDetailsDueDate <= autoPaydate2
  );

  function buildPayButton(
    billInfoDetails: BillInfo | null | undefined,
    autoPayDetails: AutoPayInfo | null,
  ) {
    const isEnrolledInAutopay = Boolean(autoPayDetails?.isEnrolled);

    if (isEnrolledInAutopay) {
      return {
        text: t('CHANGE_AUTO_PAY_SETTINGS'),
        route: ROUTES.AUTO_PAY,
        enabled: true,
        colorStyle: colors.white,
        textStyle: colors.sparkBlue,
        testid: 'auto-pay-button',
      };
    } else {
      return {
        text: t('PAY_BILL'),
        route: ROUTES.PAYMENT,
        enabled: Number(billInfoDetails?.amountDue) > 0,
        colorStyle: colors.orange,
        textStyle: colors.white,
        testid: 'pay-bill-button',
      };
    }
  }

  const convertedStartDate = toDateString(
    viewBillDetails?.billingPeriodStartDate,
    'MM/dd/yyyy',
  );
  const convertedEndDate = toDateString(
    viewBillDetails?.billingPeriodEndDate,
    'MM/dd/yyyy',
  );
  const { t, richT, inlineRichT } = useTranslation();
  const classes = useStyles();
  const message = buildPaymentMessage(billInfo);
  const dueDateInfo = buildPaymentDate(billInfo, autoPay);
  const payButton = buildPayButton(billInfo, autoPay);
  const location = useLocation();
  const currentPath = location.pathname;

  const contents = (
    <Fragment>
      <Grid container direction="row" className={classes.container}>
        <Grid
          item
          xs={isMobile ? 12 : 6}
          className={!isMobile ? classes.columnview : classes.mobileColumnview}
        >
          <Grid item xs={12}>
            <Typography
              className={classes.colorTextPrimary}
              component="span"
              variant="h1"
            >
              {toSentenceCase(t('CURRENT_BILL'))}
            </Typography>
          </Grid>

          <Grid container className={classes.boxContainer}>
            <Grid item xs={12}>
              {isPendingDisconnect ? (
                <Typography
                  className={classes.colorTextPastdue}
                  data-testid="past-due-label"
                >
                  {t('PAST_DUE')}
                </Typography>
              ) : Boolean(autoPay?.isEnrolled) ? (
                <Typography
                  className={classes.colorTextPrimary}
                  data-testid="auto-pay-label"
                >
                  {t('AUTO_PAY_AMOUNT')}
                </Typography>
              ) : (
                <Typography
                  className={classes.colorTextPrimary}
                  data-testid="amount-due-label"
                >
                  {t('AMOUNT_DUE')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                className={
                  isPendingDisconnect
                    ? classes.colorTextPastdue
                    : classes.colorTextPrimary
                }
                variant="h1"
                style={{ fontWeight: 'bold' }}
              >
                {`${
                  billInfo?.amountDue !== null &&
                  billInfo?.amountDue !== undefined
                    ? '$' +
                      toCurrencyDisplayFormat(
                        billInfo?.amountDue,
                        true,
                        amountCreditDisplay,
                      )
                    : '--'
                }`}
              </Typography>
            </Grid>
            <Grid item container direction="row" justify="center" spacing={1}>
              <Grid item>
                <Typography
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {toSentenceCase(dueDateInfo.title) + ' '}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  data-testid="current-bill-pay-summary-due-date"
                  style={{ fontWeight: 'bold' }}
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {dueDateInfo.value}
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              {viewBillDetails && viewBillDetails.billingPeriodStartDate ? (
                <Typography
                  className={classes.datesStyles}
                  variant="h6"
                  data-testid="service-startdate-enddate-message"
                >
                  {richT('SERVICE_FROM_START_END_DATE', {
                    START_DATE: convertedStartDate,
                    END_DATE: convertedEndDate,
                  })}
                </Typography>
              ) : (
                <Typography
                  className={classes.colorTextPrimary}
                  variant="h6"
                  data-testid="no-service-message"
                >
                  {richT('SERVICE_NOT_AVAILABLE')}
                </Typography>
              )}
            </Grid>
            {isPendingDisconnect && (
              <Grid direction="row" style={{ marginTop: '0.5em' }}>
                <Typography
                  className={classes.colorTextPrimary}
                  variant="body2"
                  data-testid="past-due-message"
                >
                  {t('PAST_DUE_MESSAGE', {
                    CURRENT_CHARGES: String(
                      viewBillDetails?.totalCurrentCharges,
                    ),
                    PAST_DUE: String(viewBillDetails?.totalBalanceAfterBill),
                  })}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={isMobile ? 12 : 6}
          className={!isMobile ? classes.columnview : classes.mobileColumnview}
        >
          <Grid item xs={12}>
            <Typography variant="body2" style={{ fontSize: '1rem' }}>
              {t('CURRENT_BILL_PAY_BILL_TEXT')}
            </Typography>
          </Grid>
          {Boolean(viewBillDetails) && (
            <Grid
              item
              xs={12}
              style={{ marginTop: '1rem' }}
              data-testid="payment-message"
            >
              <Typography variant="body2" style={{ fontSize: '1rem' }}>
                {message}
              </Typography>
            </Grid>
          )}
          {billInfo && billInfo.billDetails && (
            <Grid item style={{ marginTop: '1rem' }}>
              <Button
                fullWidth
                data-testid={payButton.testid}
                onClick={() => navigate(payButton.route)}
                disabled={!payButton.enabled}
                size={'large'}
                variant="outlined"
                style={{
                  whiteSpace: 'nowrap',
                  background: payButton.enabled
                    ? payButton.colorStyle
                    : colors.lightGray1,
                  color: payButton.textStyle,
                  borderRadius: isMobile ? '50px' : '4px',
                }}
              >
                {payButton.text}
              </Button>
            </Grid>
          )}
          {currentPath !== ROUTES.VIEW_BILL &&
            currentPath !== ROUTES.QUICK_VIEWBILL && (
              <Grid item style={{ marginTop: '1rem' }}>
                <Button
                  fullWidth
                  data-testid={'view_bill_button'}
                  onClick={() => navigate(ROUTES.VIEW_BILL)}
                  size={'large'}
                  variant="outlined"
                  style={{
                    whiteSpace: 'nowrap',
                    background: colors.sparkBlue,
                    color: colors.white,
                    borderRadius: isMobile ? '50px' : '4px',
                  }}
                >
                  {t('VIEW_BILL_DETAILS')}
                </Button>
              </Grid>
            )}
        </Grid>
        {autoPay?.isEnrolled &&
          AccountType.Res &&
          Number(billInfo?.amountDue) > 0 &&
          isDateBetweenAutoPayDates && (
            <div>
              <Typography
                variant="body2"
                className={
                  isMobile
                    ? classes.tempBillingMessageMob
                    : classes.tempBillingMessage
                }
              >
                {inlineRichT('TEMPORARY_BILLING_MESSAGE')}
              </Typography>
            </div>
          )}
      </Grid>
    </Fragment>
  );

  return contents;
};
export default ResCurrentBillPaymentSummary;
